/**
 * User preferences that application code actually reads.
 *
 * This store once carried seven groups of "analysis tuning" settings —
 * spatial grids, dwell radii, route similarity, performance caps — none of
 * which were consumed by anything. They were sliders wired to a graveyard,
 * and an operator who adjusted them before an exercise believed they had
 * changed something. Every key kept here has a named consumer; if you add a
 * key, wire it the same day or not at all.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TemporalSettings {
  /** Wall-clock timezone for schedules, query windows and dashboards. */
  preferredTimezone: string;
}

/** Force-graph defaults for the Link Analysis network view. */
export interface LinkAnalysisSettings {
  nodeSize: number;
  linkDistance: number;
  chargeStrength: number;
  linkCurvature: number;
  linkOpacity: number;
}

export interface VisualizationSettings {
  linkAnalysis: LinkAnalysisSettings;
}

export interface AppearanceSettings {
  themeMode: 'light' | 'soft-light' | 'soft-dark' | 'dark' | 'system';
  colorScheme: string;
  customPrimaryColor: string;
  customSecondaryColor: string;
  /** Dashboard builder: render widgets without their chrome. */
  hideWidgetHeaders: boolean;
}

export interface AppSettings {
  temporal: TemporalSettings;
  visualization: VisualizationSettings;
  appearance: AppearanceSettings;
}

export const defaultSettings: AppSettings = {
  temporal: {
    preferredTimezone: 'local',
  },
  visualization: {
    linkAnalysis: {
      nodeSize: 8,
      linkDistance: 100,
      chargeStrength: -300,
      linkCurvature: 0.8,
      linkOpacity: 0.30,
    },
  },
  appearance: {
    themeMode: 'dark',
    colorScheme: 'endurion',
    customPrimaryColor: '#2E86AB',
    customSecondaryColor: '#A23B72',
    hideWidgetHeaders: false,
  },
};

interface SettingsStore {
  settings: AppSettings;

  updateTemporal: (updates: Partial<TemporalSettings>) => void;
  updateLinkAnalysis: (updates: Partial<LinkAnalysisSettings>) => void;
  updateAppearance: (updates: Partial<AppearanceSettings>) => void;
  resetDefaults: () => void;
}

/**
 * Rebuild a valid settings object from whatever was persisted.
 *
 * The localStorage blob may predate the pruning and carry the old seven-group
 * shape, or predate `linkAnalysis` entirely. Group-by-group defaults mean a
 * stale blob can never leave a consumer reading `undefined`.
 */
function settled(persisted: unknown): AppSettings {
  const p = ((persisted as { settings?: Partial<AppSettings> })?.settings ?? {}) as {
    temporal?: Partial<TemporalSettings>;
    visualization?: { linkAnalysis?: Partial<LinkAnalysisSettings> };
    appearance?: Partial<AppearanceSettings>;
  };
  return {
    temporal: { ...defaultSettings.temporal, ...(p.temporal || {}) },
    visualization: {
      linkAnalysis: {
        ...defaultSettings.visualization.linkAnalysis,
        ...(p.visualization?.linkAnalysis || {}),
      },
    },
    appearance: { ...defaultSettings.appearance, ...(p.appearance || {}) },
  };
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      settings: defaultSettings,

      updateTemporal: (updates: Partial<TemporalSettings>) =>
        set((state: SettingsStore) => ({
          settings: { ...state.settings, temporal: { ...state.settings.temporal, ...updates } },
        })),

      updateLinkAnalysis: (updates: Partial<LinkAnalysisSettings>) =>
        set((state: SettingsStore) => ({
          settings: {
            ...state.settings,
            visualization: {
              linkAnalysis: { ...state.settings.visualization.linkAnalysis, ...updates },
            },
          },
        })),

      updateAppearance: (updates: Partial<AppearanceSettings>) =>
        set((state: SettingsStore) => ({
          settings: { ...state.settings, appearance: { ...state.settings.appearance, ...updates } },
        })),

      resetDefaults: () =>
        set({ settings: defaultSettings }),
    }),
    {
      name: 'app-settings',
      partialize: (state: SettingsStore) => ({ settings: state.settings }),
      merge: (persisted, current) => ({
        ...current,
        settings: settled(persisted),
      }),
    }
  )
);
